import React from 'react';
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { Modal, Badge, Spinner } from '../ui';
import { formatDuration } from '../charts/chartTheme';
import { formatDateTime, formatCurrency, formatPhone } from '../../utils/helpers';
import type { InsightsLead, InsightsLeadEvent } from '../../types';

interface LeadDetailModalProps {
  lead: InsightsLead | null;
  timeline: InsightsLeadEvent[];
  isLoadingTimeline: boolean;
  onClose: () => void;
}

const STATUS_VARIANT = {
  converted: 'success',
  abandoned: 'danger',
  active: 'warning',
} as const;

const EVENT_LABELS: Record<string, string> = {
  session_start: 'Opened checkout',
  step_view: 'Viewed step',
  step_complete: 'Completed step',
  field_complete: 'Filled in',
  field_error: 'Validation error',
  coupon_applied: 'Applied coupon',
  shipping_selected: 'Selected shipping',
  order_submit: 'Submitted order',
  order_complete: 'Order placed',
  order_error: 'Order failed',
  exit: 'Left the page',
};

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <span className="text-xs uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
      {label}
    </span>
    {/* `0` is a real value here (e.g. zero fields completed), so test for empty explicitly */}
    <span className="text-sm text-secondary-900 dark:text-secondary-50 text-right">
      {value === null || value === undefined || value === '' ? '—' : value}
    </span>
  </div>
);

export const LeadDetailModal: React.FC<LeadDetailModalProps> = ({
  lead,
  timeline,
  isLoadingTimeline,
  onClose,
}) => {
  if (!lead) return null;

  const address = [lead.address1, lead.address2, lead.city, lead.state, lead.zipCode]
    .filter(Boolean)
    .join(', ');

  return (
    <Modal
      isOpen={Boolean(lead)}
      onClose={onClose}
      size="xl"
      title={lead.fullName || 'Anonymous visitor'}
      description={`Checkout started ${formatDateTime(lead.createdAt)}`}
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={STATUS_VARIANT[lead.status]} dot>
            {lead.status === 'converted'
              ? 'Converted'
              : lead.status === 'abandoned'
              ? 'Abandoned'
              : 'In progress'}
          </Badge>
          <Badge variant="secondary">Reached: {lead.furthestStepLabel}</Badge>
          {lead.isReachable && <Badge variant="primary">Reachable</Badge>}
          {lead.errorCount > 0 && (
            <Badge variant="warning">
              {lead.errorCount} form error{lead.errorCount === 1 ? '' : 's'}
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Contact — the reason this record exists */}
          <div className="rounded-xl border border-secondary-100 dark:border-secondary-700 p-4">
            <h4 className="text-sm font-semibold text-secondary-900 dark:text-secondary-50 mb-3">
              Contact details captured
            </h4>
            <div className="space-y-3">
              <div className="flex items-center gap-2.5 text-sm">
                <EnvelopeIcon className="h-4 w-4 shrink-0 text-secondary-400" />
                {lead.email ? (
                  <a
                    href={`mailto:${lead.email}`}
                    className="text-primary-600 dark:text-primary-400 hover:underline truncate"
                  >
                    {lead.email}
                  </a>
                ) : (
                  <span className="text-secondary-400 dark:text-secondary-500">Not provided</span>
                )}
              </div>
              <div className="flex items-center gap-2.5 text-sm">
                <PhoneIcon className="h-4 w-4 shrink-0 text-secondary-400" />
                {lead.phone ? (
                  <a
                    href={`tel:${lead.phone}`}
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {formatPhone(lead.phone)}
                  </a>
                ) : (
                  <span className="text-secondary-400 dark:text-secondary-500">Not provided</span>
                )}
              </div>
              <div className="flex items-start gap-2.5 text-sm">
                <MapPinIcon className="h-4 w-4 shrink-0 mt-0.5 text-secondary-400" />
                <span className="text-secondary-700 dark:text-secondary-300">
                  {address || <span className="text-secondary-400 dark:text-secondary-500">Not provided</span>}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-secondary-100 dark:border-secondary-700">
              <DetailRow label="Date of birth" value={lead.dateOfBirth} />
              <DetailRow label="Gender" value={lead.gender} />
              <DetailRow label="Fields completed" value={lead.fieldsCaptured.length} />
            </div>
          </div>

          {/* Session context */}
          <div className="rounded-xl border border-secondary-100 dark:border-secondary-700 p-4">
            <h4 className="text-sm font-semibold text-secondary-900 dark:text-secondary-50 mb-3">
              Session
            </h4>
            <DetailRow label="Time on checkout" value={formatDuration(lead.activeSeconds)} />
            <DetailRow label="Last activity" value={formatDateTime(lead.lastActivityAt)} />
            <DetailRow
              label="Cart value"
              value={lead.cartValue !== null ? formatCurrency(lead.cartValue) : undefined}
            />
            <DetailRow label="Coupon" value={lead.couponCode} />
            <DetailRow label="Source" value={lead.utmSource || lead.referrer || 'Direct'} />
            <DetailRow label="Campaign" value={lead.utmCampaign} />
            <DetailRow
              label="Device"
              value={[lead.deviceType, lead.browser].filter(Boolean).join(' · ')}
            />
            {lead.orderId && (
              <div className="mt-3 pt-3 border-t border-secondary-100 dark:border-secondary-700">
                <Link
                  to={`/orders/${lead.orderId}`}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                >
                  View order {lead.orderCode ? `#${lead.orderCode}` : ''}
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Activity timeline */}
        <div>
          <h4 className="text-sm font-semibold text-secondary-900 dark:text-secondary-50 mb-3">
            Activity timeline
          </h4>

          {isLoadingTimeline ? (
            <div className="flex justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : timeline.length === 0 ? (
            <p className="text-sm text-secondary-500 dark:text-secondary-400 py-4">
              No recorded activity for this session.
            </p>
          ) : (
            <ol className="max-h-64 overflow-y-auto scrollbar-thin pr-2 space-y-0">
              {timeline.map((event, index) => {
                const isError = event.type === 'field_error' || event.type === 'order_error';
                return (
                  <li key={`${event.occurredAt}-${index}`} className="flex gap-3 pb-3 last:pb-0">
                    <div className="flex flex-col items-center">
                      <span
                        className={
                          isError
                            ? 'mt-1.5 h-2 w-2 rounded-full bg-danger-500'
                            : 'mt-1.5 h-2 w-2 rounded-full bg-primary-400 dark:bg-primary-500'
                        }
                      />
                      {index < timeline.length - 1 && (
                        <span className="w-px flex-1 bg-secondary-200 dark:bg-secondary-700" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0 pb-1">
                      <p className="text-sm text-secondary-900 dark:text-secondary-50">
                        {isError && (
                          <ExclamationTriangleIcon className="inline h-4 w-4 mr-1 -mt-0.5 text-danger-500" />
                        )}
                        {EVENT_LABELS[event.type] || event.type}
                        {event.fieldLabel && (
                          <span className="text-secondary-500 dark:text-secondary-400">
                            {' '}
                            · {event.fieldLabel}
                          </span>
                        )}
                        {event.step && !event.fieldLabel && (
                          <span className="text-secondary-500 dark:text-secondary-400"> · {event.step}</span>
                        )}
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 inline-flex items-center gap-1">
                        <ClockIcon className="h-3 w-3" />
                        {formatDateTime(event.occurredAt)}
                        {typeof event.metadata?.message === 'string' && (
                          <span className="ml-1 text-danger-600 dark:text-danger-400">
                            — {String(event.metadata.message)}
                          </span>
                        )}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default LeadDetailModal;
